import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate, useParams } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'

import type { OrderingContext } from '@/bindings/OrderingContext'
import type { OrderingMode } from '@/bindings/OrderingMode'
import type { PlacedOrder } from '@/bindings/PlacedOrder'
import type { TrackedOrder } from '@/bindings/TrackedOrder'
import { Button } from '@/components/button'
import { buttonClass } from '@/components/button-class'
import { Card, Page } from '@/components/layout'
import { branchPath } from '@/features/landing/api'
import { orderTrackingPath, orderingContextQuery, orderingKeys } from '@/features/ordering/api'
import { EMPTY_CART, cartTotalMinor, prune, quantityOf, setQuantity, useCart } from '@/features/ordering/cart'
import { CartSheet } from '@/features/ordering/cart-sheet'
import { useCustomerAuth } from '@/features/ordering/customer-auth'
import { CustomerHeader, CustomerLoadError, CustomerLoading, CustomerShell } from '@/features/ordering/customer-chrome'
import { MenuList } from '@/features/ordering/menu-list'
import { whereLabel } from '@/features/ordering/order-rules'
import { minorUnits } from '@/lib/currencies'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { formatPrice } from '@/lib/menu-format'
import { rememberMyOrder } from '@/lib/my-orders'
import { fromMinor } from '@/lib/quantity'
import { loginPathFor, loginSearchFor } from '@/lib/return-path'

const TAKE_AWAY_ROUTE = '/$restaurantSlug/$branchSlug/order'
const TABLE_ROUTE = '/$restaurantSlug/$branchSlug/t/$tableCode'

/** `/<r>/<b>/order` — the take-away QR (G3). */
export function TakeAwayOrderingPage() {
  const { restaurantSlug, branchSlug } = useParams({ from: TAKE_AWAY_ROUTE })
  return <OrderingPage restaurantSlug={restaurantSlug} branchSlug={branchSlug} />
}

/** `/<r>/<b>/t/<code>` — a table's QR: every order joins the table's session (G1/G2). */
export function TableOrderingPage() {
  const { restaurantSlug, branchSlug, tableCode } = useParams({ from: TABLE_ROUTE })
  return <OrderingPage restaurantSlug={restaurantSlug} branchSlug={branchSlug} tableCode={tableCode} />
}

/**
 * The customer ordering page (PRD "Making order pages", G6): minimal chrome
 * (T13), the menu, a cart bar, and the cart sheet. Gates come from the
 * context (G2): not taking orders → a notice naming the reason and the way
 * to the branch page (2026-09-15); Login mode → the login comes before the
 * menu (2026-09-05), with the way back; PIN → a field in the sheet. Placing
 * lands on G7's tracking page by the order's code.
 */
function OrderingPage({ restaurantSlug, branchSlug, tableCode }: { restaurantSlug: string; branchSlug: string; tableCode?: string }) {
  const t = useT()
  const auth = useCustomerAuth()
  const context = useQuery(orderingContextQuery(restaurantSlug, branchSlug, tableCode))

  if (context.isPending) return <CustomerLoading />
  if (context.error) return <CustomerLoadError error={context.error} notFound={t('order.notFound')} retry={() => void context.refetch()} />

  const data = context.data
  const blocked = !data.branch.accepts_orders
  // A blocked page needs no `/me` verdict; otherwise Login mode waits for it.
  const loginFirst = !blocked && data.branch.ordering_mode === 'login' && auth.status !== 'authenticated'
  if (loginFirst && auth.status === 'unknown') return <CustomerLoading />

  return (
    <CustomerShell color={data.restaurant.color} bottomBar={!blocked && !loginFirst}>
      <CustomerHeader restaurant={data.restaurant.name} branch={data.branch.name} auth={auth} />
      {blocked ? (
        <NotTakingOrders restaurantSlug={restaurantSlug} branchSlug={branchSlug} mode={data.branch.ordering_mode} />
      ) : loginFirst ? (
        <LoginFirst />
      ) : (
        <Ordering restaurantSlug={restaurantSlug} branchSlug={branchSlug} tableCode={tableCode} context={data} anonymous={auth.status !== 'authenticated'} />
      )}
    </CustomerShell>
  )
}

interface OrderingProps {
  restaurantSlug: string
  branchSlug: string
  tableCode: string | undefined
  context: OrderingContext
  anonymous: boolean
}

/** The menu, the cart bar, and the sheet. A block unmounts it, ending the attempt with the sheet; the cart itself waits in storage. */
function Ordering({ restaurantSlug, branchSlug, tableCode, context: data, anonymous }: OrderingProps) {
  const t = useT()
  const language = useLanguage()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const storageKey = `romi.cart.${restaurantSlug}/${branchSlug}/${tableCode === undefined ? 'order' : `t:${tableCode}`}`
  const { cart, update, attempt, endAttempt } = useCart(storageKey)
  const [cartOpen, setCartOpen] = useState(false)

  const items = useMemo(() => new Map(data.items.map((item) => [item.id, item])), [data.items])
  useEffect(() => {
    update((cart) => prune(cart, items))
  }, [items, update])
  const total = fromMinor(cartTotalMinor(cart, items, data.branch.currency), data.branch.currency)

  const onPlaced = (order: PlacedOrder) => {
    rememberMyOrder(restaurantSlug, branchSlug, order.code)
    // The tracking page (G7) opens on this and refetches; the chrome comes from the context.
    const tracked: TrackedOrder = { restaurant: data.restaurant, branch: data.branch, order, feedback: null }
    queryClient.setQueryData(orderingKeys.order(restaurantSlug, branchSlug, order.code), tracked)
    update(() => EMPTY_CART)
    void navigate({ to: orderTrackingPath(restaurantSlug, branchSlug, order.code) })
  }

  return (
    <>
      <Page width="md" className="flex-1">
        <p className="bg-accent-soft text-accent-ink mb-2 rounded-lg px-4 py-2 font-semibold">{whereLabel(t, data.table?.name)}</p>
        <MenuList
          categories={data.categories}
          items={data.items}
          view={{ main: data.restaurant.main_language, currency: data.branch.currency, readOnly: false }}
          quantityOf={(itemId) => quantityOf(cart, itemId)}
          onQuantity={(itemId, quantity) => update((cart) => setQuantity(cart, itemId, quantity))}
        />
      </Page>
      {cart.lines.length > 0 && !cartOpen && (
        <CartBar count={cart.lines.length} total={formatPrice(total, minorUnits(data.branch.currency), language)} onOpen={() => setCartOpen(true)} />
      )}
      <CartSheet
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        context={data}
        items={items}
        tableCode={tableCode}
        cart={cart}
        update={update}
        attempt={attempt}
        endAttempt={endAttempt}
        anonymous={anonymous}
        onPlaced={onPlaced}
      />
    </>
  )
}

/**
 * Not taking orders (2026-09-15): name whose decision it is — the shop is
 * closed, or the branch turned QR ordering off — so a scan that "worked" but
 * cannot order does not read as Romi failing. The branch page keeps the menu.
 */
function NotTakingOrders({ restaurantSlug, branchSlug, mode }: { restaurantSlug: string; branchSlug: string; mode: OrderingMode }) {
  const t = useT()
  return (
    <Page width="sm" className="pt-10">
      <Card className="flex flex-col gap-4 p-6">
        <p role="alert">{t(mode === 'closed' ? 'order.qrOrderingOff' : 'order.shopClosed')}</p>
        <Link to={branchPath(restaurantSlug, branchSlug)} className={buttonClass('primary')}>
          {t('order.branchPage')}
        </Link>
      </Card>
    </Page>
  )
}

/** Login mode, anonymous: the requirement up front, before anyone picks a dish; the cart waits in storage. */
function LoginFirst() {
  const t = useT()
  const location = useLocation()
  return (
    <Page width="sm" className="pt-10">
      <Card className="flex flex-col gap-4 p-6">
        <p>{t('order.loginRequired')}</p>
        <Link to={loginPathFor('restaurant')} search={loginSearchFor('restaurant', location)} className={buttonClass('primary')}>
          {t('order.loginToOrder')}
        </Link>
      </Card>
    </Page>
  )
}

/** The fixed strip above the menu once something is in the cart. */
function CartBar({ count, total, onOpen }: { count: number; total: string; onOpen: () => void }) {
  const t = useT()
  return (
    <div className="bg-surface border-border fixed inset-x-0 bottom-0 z-30 border-t px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <div className="mx-auto flex max-w-2xl items-center justify-between gap-3">
        <span>
          <span className="font-medium">{t('order.itemCount', { count })}</span> <span className="text-muted">· {total}</span>
        </span>
        <Button onClick={onOpen}>{t('order.viewCart')}</Button>
      </div>
    </div>
  )
}
